import { useTranslation } from 'react-i18next';
import type { Generatore } from '@emacomfort/domain';
import { useLettura, useObsoleto, useStato } from '@/app/DeviceProvider';
import { useFormato } from '@/app/formato';
import { cx } from '@/ui';
import { useAttuatore, useCanalePerFunzione, useMediaAmbiente, useValoreFunzione, useZone, useZoneInDomanda } from '../comune';
import s from '../features.module.css';

/**
 * Schema sinottico (U1): generatori (1–4) → accumulo → circolatore → casa.
 * Il flusso si anima solo con circolatore effettivamente attivo (RF-04). Ogni
 * stato ha anche testo: il colore non è mai l'unico segnale (RNF-04).
 */

const W = 1200;
const H = 320;

function NodoGeneratore({ g, y, h, attivo }: { g: Generatore; y: number; h: number; attivo: boolean }) {
  const { t } = useTranslation();
  const f = useFormato();
  // Un generatore puo' esistere senza sonda di mandata (`canaleMandata: ''`):
  // il generatore c'e', la sua temperatura non e' misurata. Il posto resta
  // vuoto invece di mostrare un trattino che sembra una sonda guasta.
  const misurata = Boolean(g.canaleMandata);
  const mandata = useLettura(g.canaleMandata || undefined);
  const compatto = h < 90;
  return (
    <g transform={`translate(40 ${y})`}>
      <rect className={cx(s.nodo, attivo && s.nodoAttivo)} width={230} height={h - 10} rx={14} />
      <text className={s.nodoEtichetta} x={16} y={compatto ? 24 : 28}>
        {t(`generatore.${g.tipo}`)}
      </text>
      {!compatto && (
        <text className={s.nodoTesto} x={16} y={54}>
          {g.nome.length > 22 ? `${g.nome.slice(0, 21)}…` : g.nome}
        </text>
      )}
      {misurata && (
        <text className={cx(s.nodoValore, mandata.obsoleto && s.nodoValoreObsoleto)} x={16} y={compatto ? h - 22 : h - 26} style={{ fontSize: compatto ? 20 : 26 }}>
          {f.temp(mandata.valore)}
        </text>
      )}
      <text className={attivo ? s.nodoStatoOk : s.nodoStatoOff} x={214} y={compatto ? h - 22 : h - 26} textAnchor="end">
        {attivo ? t('pannello.acceso').toUpperCase() : t('pannello.spento').toUpperCase()}
      </text>
    </g>
  );
}

export function Sinottico() {
  const { t } = useTranslation();
  const f = useFormato();
  const impianto = useStato((st) => st.impianto);
  const attuatori = useStato((st) => st.attuatori);
  const zoneInDomanda = useZoneInDomanda();
  const zone = useZone();
  const media = useMediaAmbiente();
  const { obsoleto } = useObsoleto();
  const circCanale = useCanalePerFunzione('CIRCOLATORE');
  const circolatore = useAttuatore(circCanale?.id);
  const pufferAlto = useValoreFunzione('PUFFER_ALTO');
  const pufferBasso = useValoreFunzione('PUFFER_BASSO');
  const mandata = useValoreFunzione('MANDATA_COLLETTORE');
  const energia = useStato((st) => (st.impianto?.energia.sorgenteId ? st.energia[st.impianto.energia.sorgenteId] : undefined));
  const sorgenteFv = useStato((st) => (st.impianto?.energia.sorgenteId ? st.sorgenti[st.impianto.energia.sorgenteId] : undefined));

  if (!impianto) return null;

  const generatori = [...impianto.generatori].sort((a, b) => a.priorita - b.priorita);
  const n = Math.max(1, generatori.length);
  const hGen = (H - 20) / n;
  const circolatoreAttivo = !obsoleto && circolatore?.stato === 'attivo';
  const pompaDiCalore = generatori.find((g) => g.tipo === 'POMPA_DI_CALORE');
  const mostraFv = impianto.energia.presente;
  const zoneAttive = zone.filter((z) => z.abilitata).length;
  const inDomanda = zoneInDomanda.length;
  const hannoPuffer = pufferAlto.canale !== undefined;

  const generatoreAttivo = (g: Generatore) => !obsoleto && (g.canaleComando ? attuatori[g.canaleComando]?.stato === 'attivo' : circolatoreAttivo);

  // 🚨 `null`, non 0.5. Il livello del serbatoio e' il disegno di una MISURA:
  // senza sonda, o con la sonda muta durante un'interruzione, non c'e' niente
  // da disegnare. Il vecchio 0.5 dipingeva un serbatoio mezzo pieno e caldo
  // sotto la scritta "Nessuna sonda sul puffer" — una figura inventata, per di
  // piu' sovrapposta al testo che diceva di non saperlo.
  const livello =
    hannoPuffer && pufferAlto.valore !== null && !pufferAlto.obsoleto
      ? Math.max(0.15, Math.min(0.95, (pufferAlto.valore - 25) / 35))
      : null;

  return (
    <svg className={s.sinottico} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t('pannello.impianto')}>
      {/* tubi */}
      <path className={cx(s.tubo, s.tuboCaldo)} d={`M270 ${H / 2 - 30} H 470`} />
      <path className={cx(s.tubo, s.tuboFreddo)} d={`M270 ${H / 2 + 30} H 470`} />
      <path className={cx(s.tubo, s.tuboCaldo)} d={`M640 ${H / 2 - 30} H 740 M 820 ${H / 2 - 30} H 940`} />
      <path className={cx(s.tubo, s.tuboFreddo)} d={`M640 ${H / 2 + 30} H 940`} />
      {/* flusso animato (RF-04) */}
      <path className={cx(s.flusso, circolatoreAttivo && s.flussoAttivo)} d={`M270 ${H / 2 - 30} H 470 M640 ${H / 2 - 30} H 740 M 820 ${H / 2 - 30} H 940`} />
      <path className={cx(s.flusso, circolatoreAttivo && s.flussoAttivo, s.flussoInverso)} d={`M940 ${H / 2 + 30} H 640 M470 ${H / 2 + 30} H 270`} />

      {/* fotovoltaico (solo dove esiste) */}
      {mostraFv && pompaDiCalore && (
        <g transform="translate(300 12)">
          <path className={cx(s.tubo, s.tuboSolare)} d={`M 60 70 V ${H / 2 - 52 - 12} H -30`} />
          <rect className={s.pannelloSolare} x={0} y={0} width={120} height={70} rx={8} />
          <text className={s.nodoEtichetta} x={10} y={22} style={{ fill: 'var(--solar)' }}>
            {t('pannello.energia.produzione')}
          </text>
          <text className={cx(s.nodoValore, (!sorgenteFv?.raggiungibile || obsoleto) && s.nodoValoreObsoleto)} x={10} y={56} style={{ fontSize: 24 }}>
            {sorgenteFv?.raggiungibile ? f.kw(energia?.produzioneW ?? null) : '— —'}
          </text>
        </g>
      )}

      {/* generatori */}
      {generatori.map((g, i) => (
        <NodoGeneratore key={g.id} g={g} y={10 + i * hGen} h={hGen} attivo={generatoreAttivo(g)} />
      ))}

      {/* accumulo */}
      <g transform={`translate(470 ${H / 2 - 110})`}>
        <rect className={s.serbatoio} width={170} height={220} rx={26} />
        {livello !== null && (
          <rect className={s.serbatoioLivello} x={4} y={4 + 212 * (1 - livello)} width={162} height={212 * livello} rx={22} />
        )}
        <text className={s.nodoEtichetta} x={85} y={28} textAnchor="middle">
          {t('pannello.accumulo')}
        </text>
        {hannoPuffer ? (
          <>
            <text className={cx(s.nodoValore, pufferAlto.obsoleto && s.nodoValoreObsoleto)} x={85} y={90} textAnchor="middle">
              {f.temp(pufferAlto.valore)}
            </text>
            <text className={s.nodoEtichetta} x={85} y={112} textAnchor="middle">
              {t('pannello.pufferAlto')}
            </text>
            <text className={cx(s.nodoValore, pufferBasso.obsoleto && s.nodoValoreObsoleto)} x={85} y={170} textAnchor="middle">
              {f.temp(pufferBasso.valore)}
            </text>
            <text className={s.nodoEtichetta} x={85} y={192} textAnchor="middle">
              {t('pannello.pufferBasso')}
            </text>
          </>
        ) : (
          <>
            <text className={s.nodoTesto} x={85} y={110} textAnchor="middle" style={{ fontSize: 16 }}>
              {t('pannello.accumuloSenzaSonda')}
            </text>
            {mandata.canale && (
              <>
                <text className={s.nodoEtichetta} x={85} y={150} textAnchor="middle">
                  {t('pannello.mandata')}
                </text>
                <text className={cx(s.nodoValore, mandata.obsoleto && s.nodoValoreObsoleto)} x={85} y={184} textAnchor="middle">
                  {f.temp(mandata.valore)}
                </text>
              </>
            )}
          </>
        )}
      </g>

      {/* circolatore */}
      <g transform={`translate(780 ${H / 2 - 30})`}>
        <circle className={cx(s.nodo, circolatoreAttivo && s.nodoAttivo)} r={40} />
        <path d="M -14 -14 L 14 0 L -14 14 Z" fill={circolatoreAttivo ? 'var(--ok)' : 'var(--text-3)'}>
          {circolatoreAttivo && <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="1.4s" repeatCount="indefinite" />}
        </path>
        <text className={s.nodoEtichetta} x={0} y={-56} textAnchor="middle">
          {t('pannello.circolatore')}
        </text>
        {/* Sotto il tubo di ritorno (che passa a y=60 nel sistema di questo
            gruppo), non sopra: a y=66 lo stato finiva sulla linea. */}
        <text className={circolatoreAttivo ? s.nodoStatoOk : s.nodoStatoOff} x={0} y={90} textAnchor="middle">
          {circolatoreAttivo ? t('pannello.acceso').toUpperCase() : t('pannello.spento').toUpperCase()}
        </text>
        {circolatore?.modulazione !== undefined && circolatoreAttivo && (
          <text className={s.nodoEtichetta} x={0} y={112} textAnchor="middle">
            {f.pct(circolatore.modulazione)}
          </text>
        )}
      </g>

      {/* casa */}
      <g transform={`translate(940 ${H / 2 - 120})`}>
        <path className={cx(s.nodo, inDomanda > 0 && !obsoleto && s.nodoAttivo)} d="M 110 0 L 220 90 V 240 H 0 V 90 Z" />
        <text className={s.nodoEtichetta} x={110} y={120} textAnchor="middle">
          {t('pannello.mediaAmbiente')}
        </text>
        <text className={cx(s.nodoValore, obsoleto && s.nodoValoreObsoleto)} x={110} y={162} textAnchor="middle" style={{ fontSize: 32 }}>
          {f.temp(media)}
        </text>
        <text className={inDomanda > 0 && !obsoleto ? s.nodoStatoOk : s.nodoStatoOff} x={110} y={200} textAnchor="middle">
          {t('pannello.zoneAttive', { n: obsoleto ? '—' : inDomanda, tot: zoneAttive })}
        </text>
      </g>
    </svg>
  );
}
